import { Box, IconButton, Icons, Text, useMedia } from '@opendoor/bricks-next';

import { useReturnExperienceContext } from 'helpers/ReturnExperienceContext';

import { ExperienceType } from '../../helpers/returnExperience';
import { CONSUMER_FE_URL, COSMOS_URL } from '../globals';

type CTAButtonProps = {
  experience: ExperienceType;
  loading?: boolean;
  onClick?: () => void;
  url?: string;
  actionText: string;
  isDirectMail?: boolean;
};

const AltCTAButton = (props: CTAButtonProps): JSX.Element => {
  return (
    <IconButton
      analyticsName={`cosmos-return-experience-${ExperienceType[props.experience]}-icon-button`}
      icon={{ Component: Icons.RightArrow, label: `${props.actionText}` }}
      onPress={() => props.url && window.location.assign(props.url)}
      size="small"
      variant="default"
      trackImpression
    />
  );
};

const getActiveOfferExpiryText = (daysToExpire = 10) => {
  let expiryText = 'expires in 2 days';
  if (daysToExpire < 1) {
    expiryText = 'expires today';
  } else if (daysToExpire < 2) {
    expiryText = 'expires in 1 day';
  } else if (daysToExpire < 3) {
    expiryText = 'expires in 2 days';
  } else if (daysToExpire === 3) {
    expiryText = 'expires in 3 days';
  }
  return expiryText;
};

type HeadlineProps = {
  experience: ExperienceType;
  currentOfferPriceCents?: number;
  previousOfferPriceCents?: number;
  daysToExpire?: number;
  smallerThanMD: boolean;
};

const Headline = (props: HeadlineProps): JSX.Element | null => {
  switch (props.experience) {
    case ExperienceType.direct_mailer:
      return <>Get started on your offer</>;
    case ExperienceType.offer_expired:
      return <>Get your updated offer{props.smallerThanMD ? '' : ' today'}</>;
    case ExperienceType.incomplete_flow:
      return <>Finish your offer{props.smallerThanMD ? '' : ' today'}</>;
    case ExperienceType.offer_increased:
      return <>Your offer increased</>;
    case ExperienceType.offer_expiring:
    case ExperienceType.offer_decreased:
    case ExperienceType.active_offer:
      return <>Your offer {getActiveOfferExpiryText(props.daysToExpire)}</>;
    case ExperienceType.home_insights:
      return <>Get back to your{props.smallerThanMD ? <br /> : ' '}home insights</>;
    default:
      return null;
  }
};

type ExperienceCTAButtonProps = {
  experience: ExperienceType;
  sellerInputUUID?: string;
  customerUUID?: string;
  addressToken?: string;
  smallerThanMD?: boolean;
  sellerFlowLastCompletedSlug?: string;
};

const ExperienceCTAButton = (props: ExperienceCTAButtonProps): JSX.Element | null => {
  let offerDashUrl = COSMOS_URL;
  if (props.addressToken) {
    // Return to sell tab in hub for returning customers
    offerDashUrl = `${CONSUMER_FE_URL}/dashboard/sell/${props.addressToken}`;
  } else if (props.sellerInputUUID) {
    offerDashUrl = `${CONSUMER_FE_URL}/dashboard/${props.sellerInputUUID}/overview`;
  }

  switch (props.experience) {
    case ExperienceType.offer_expired: {
      const baseUrl = offerDashUrl;
      const params = new URLSearchParams({
        refresh_offer: 'true',
        refresh_source: 'homepage_experience',
      }).toString();
      const refreshUrl = `${baseUrl}?${params}`;
      return (
        <AltCTAButton
          actionText={`Refresh${props.smallerThanMD ? ' ' : ' your '}offer`}
          url={refreshUrl}
          experience={props.experience}
        />
      );
    }
    case ExperienceType.incomplete_flow:
      // eslint-disable-next-line no-case-declarations
      const sellerFlowUrl = `${CONSUMER_FE_URL}/seller-lead/${props.sellerInputUUID}/${props.sellerFlowLastCompletedSlug}`;
      return (
        <AltCTAButton
          actionText={`Resume${props.smallerThanMD ? ' ' : ' your '}offer`}
          url={sellerFlowUrl}
          experience={props.experience}
        />
      );
    case ExperienceType.offer_increased:
    case ExperienceType.offer_expiring:
    case ExperienceType.offer_decreased:
    case ExperienceType.active_offer:
      return (
        <AltCTAButton
          actionText={`View${props.smallerThanMD ? ' ' : ' your '}offer`}
          url={offerDashUrl}
          experience={props.experience}
        />
      );
    case ExperienceType.home_insights:
      // eslint-disable-next-line no-case-declarations
      const homeInsightsUrl = `${CONSUMER_FE_URL}/home-insights`;
      return (
        <AltCTAButton
          actionText={`View insights`}
          url={homeInsightsUrl}
          experience={props.experience}
        />
      );
    default:
      return null;
  }
};

export const ReturnExperienceHero = () => {
  const { returnExperienceData } = useReturnExperienceContext();
  const { smallerThanSM } = useMedia();

  if (!returnExperienceData || returnExperienceData?.experienceType === null) {
    return null;
  }

  return (
    <Box
      animation="contentfulArchAnimation"
      animateOnly={['opacity', 'transform']}
      enterStyle={{ y: -30, opacity: 0 }}
      flexDirection="row"
      alignItems="center"
      justifyContent="space-between"
      top={-27}
      p={16}
      pt={43}
      analyticsName="cosmos-return-experience-address-entry-slide-in"
      backgroundColor={'$backgroundSecondary'}
      borderBottomRightRadius={20}
      borderBottomLeftRadius={20}
    >
      <Box gap={5}>
        <Text
          tag="span"
          color="$contentPrimary"
          typography="$labelSmall"
          $largerThanMD={{ typography: '$labelMedium' }}
        >
          <Headline
            experience={returnExperienceData.experienceType}
            currentOfferPriceCents={returnExperienceData.currentOfferHeadlinePriceCents}
            previousOfferPriceCents={returnExperienceData.previousOfferHeadlinePriceCents}
            daysToExpire={returnExperienceData.offerExpiresInDays}
            smallerThanMD={smallerThanSM}
          />
        </Text>
        <Text tag="span" color="$contentSecondary" typography="$bodySmall">
          {returnExperienceData.address}
        </Text>
      </Box>
      <ExperienceCTAButton
        experience={returnExperienceData.experienceType}
        sellerInputUUID={returnExperienceData.sellerInputUUID}
        customerUUID={returnExperienceData.customerUUID}
        addressToken={returnExperienceData.addressToken}
        smallerThanMD={false}
      />
    </Box>
  );
};
